import React, { useState, useEffect } from 'react';
import { ActiveCallState } from '../types';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  PhoneOff,
  Sparkles,
  ShieldCheck,
  Maximize2,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface CallModalProps {
  callState: ActiveCallState;
  onEndCall: () => void;
}

export const CallModal: React.FC<CallModalProps> = ({ callState, onEndCall }) => {
  const [isMuted, setIsMuted] = useState(callState.isMuted);
  const [isVideo, setIsVideo] = useState(callState.type === 'video');
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    // Ring effect on start
    soundEffects.playRing();
    const ringInterval = setInterval(() => {
      soundEffects.playRing();
    }, 3000);

    const timer = setInterval(() => {
      setDuration((d) => d + 1);
    }, 1000);

    return () => {
      clearInterval(ringInterval);
      clearInterval(timer);
    };
  }, []);

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl font-['Cairo'] select-none">
      <div className="relative w-full max-w-sm h-[80vh] rounded-3xl overflow-hidden bg-gradient-to-b from-[#0c1424] to-[#060911] border border-emerald-500/30 shadow-2xl flex flex-col justify-between p-6">
        {/* Top Info Bar */}
        <div className="flex items-center justify-between z-10">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] text-emerald-300 font-bold">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>مشفرة بنبض وتين</span>
          </div>

          <span className="text-xs font-mono font-bold text-white bg-black/40 px-3 py-1 rounded-full">
            {formatTimer(duration)}
          </span>
        </div>

        {/* Center Target Info & Avatar */}
        <div className="flex flex-col items-center justify-center text-center z-10 my-auto">
          {isVideo ? (
            /* Video simulation container */
            <div className="relative w-full aspect-square max-w-[260px] rounded-3xl overflow-hidden shadow-2xl border border-emerald-500/40 mb-4 bg-slate-900">
              <img
                src={callState.targetUser.avatar}
                alt={callState.targetUser.displayName}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-lg bg-black/60 backdrop-blur text-[10px] text-white">
                HD 1080p
              </div>
            </div>
          ) : (
            /* Voice pulse avatar */
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl animate-ping" />
              <div className="relative w-28 h-28 rounded-full p-1 bg-gradient-to-tr from-emerald-400 via-teal-400 to-indigo-500 shadow-2xl">
                <img
                  src={callState.targetUser.avatar}
                  alt={callState.targetUser.displayName}
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
            </div>
          )}

          <h2 className="text-lg font-bold text-white mb-0.5">{callState.targetUser.displayName}</h2>
          <span className="text-xs text-emerald-400 font-mono">@{callState.targetUser.username}</span>
          <span className="text-[11px] text-slate-400 mt-2 animate-pulse">
            {isVideo ? 'مكالمة فيديو متصلة عبر وتين' : 'مكالمة صوتية عالية النقاء'}
          </span>
        </div>

        {/* Bottom Control Bar */}
        <div className="flex items-center justify-center gap-4 z-10 pt-4">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`p-3.5 rounded-2xl transition ${
              isMuted
                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                : 'bg-[#141b27] text-white hover:bg-white/10'
            }`}
            title={isMuted ? 'إلغاء الكتم' : 'كتم الميكروفون'}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>

          <button
            onClick={() => setIsVideo(!isVideo)}
            className={`p-3.5 rounded-2xl transition ${
              !isVideo
                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                : 'bg-[#141b27] text-white hover:bg-white/10'
            }`}
            title={isVideo ? 'إيقاف الكاميرا' : 'تشغيل الكاميرا'}
          >
            {isVideo ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>

          {/* End Call Button */}
          <button
            onClick={onEndCall}
            className="p-4 rounded-3xl bg-rose-600 hover:bg-rose-500 text-white shadow-xl shadow-rose-600/30 transition active:scale-95"
            title="إنهاء المكالمة"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};
